import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, RefreshCw, Undo2, AlertTriangle } from 'lucide-react';
import { getLoadStatusClasses } from '@/lib/statusColors';

const STATUS_LABELS = {
  assigned: 'Assigned',
  at_pickup: 'At Pickup',
  in_transit: 'In Transit',
  at_delivery: 'At Delivery',
  completed: 'Completed',
  invoiced: 'Invoiced',
};

function parseAddress(address) {
  if (!address) return { street: '', city: '' };
  const parts = address.split(',').map(p => p.trim());
  return { street: parts[0] || '', city: parts[1] || '' };
}

function formatDate(dateStr) {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleString('en-CA', {
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function ActiveLoadsGrid({
  activeLoads, activeTab, setActiveTab,
  selectedLoadIds, toggleLoadSelection, selectAllLoads,
  activeSearch, setActiveSearch,
  onRefresh, onUndeliver, drivers,
}) {
  const allSelected = activeLoads.length > 0 && activeLoads.every(l => selectedLoadIds.has(l.load_id));

  const handleSelectAll = () => {
    if (allSelected) selectAllLoads([]);
    else selectAllLoads(activeLoads.map(l => l.load_id));
  };

  const selectedInGrid = activeLoads.filter(l => selectedLoadIds.has(l.load_id)).length;

  const getDriverName = (driverId) => {
    if (!driverId) return '--';
    const driver = drivers.find(d => d.user_id === driverId);
    return driver?.name || driverId.slice(-6);
  };

  return (
    <div className="flex flex-col h-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
        <div className="flex items-center justify-between px-3 pt-2 pb-1 border-b border-border bg-surface/30">
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              className="border-input text-muted-foreground h-7 text-[10px] gap-1"
              onClick={onRefresh}
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </Button>
            <Button
              variant="outline" size="sm"
              className="border-input text-muted-foreground h-7 text-[10px] gap-1 disabled:opacity-40"
              disabled={selectedInGrid === 0 || activeTab !== 'delivered'}
              onClick={onUndeliver}
            >
              <Undo2 className="w-3 h-3" /> UnDeliver
            </Button>

            <div className="w-px h-5 bg-input mx-1" />

            <TabsList className="bg-muted/50 h-7">
              <TabsTrigger value="in_progress" className="text-[10px] h-6 px-3 data-[state=active]:bg-brand-accent data-[state=active]:text-brand-accent-foreground">
                In Progress
              </TabsTrigger>
              <TabsTrigger value="delivered" className="text-[10px] h-6 px-3 data-[state=active]:bg-brand-accent data-[state=active]:text-brand-accent-foreground">
                Delivered
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[10px] text-muted-foreground">
              Total Loads: {activeLoads.length} AND Selected Loads: {selectedInGrid}
            </span>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={activeSearch}
                onChange={e => setActiveSearch(e.target.value)}
                className="bg-muted border-input text-foreground h-7 text-xs pl-7 w-48"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <TabsContent value={activeTab} className="h-full mt-0">
            <ScrollArea className="h-full">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="w-8 px-2">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                      />
                    </TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Load Number</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2 w-8">Alert</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Svc</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Driver</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Shipper Name</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Shipper Street</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Shipper City</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Consignee Name</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Consignee Street</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Consignee City</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Load Status</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2 text-right">Weight</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2 text-right">Subtotal</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Created Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeLoads.map(load => {
                    const pickup = parseAddress(load.pickup_address);
                    const delivery = parseAddress(load.delivery_address);
                    const isSelected = selectedLoadIds.has(load.load_id);
                    return (
                      <TableRow
                        key={load.load_id}
                        className={`border-border/50 cursor-pointer transition-colors ${
                          isSelected ? 'bg-brand-accent/5' : 'hover:bg-muted/30'
                        }`}
                        onClick={() => toggleLoadSelection(load.load_id)}
                      >
                        <TableCell className="px-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleLoadSelection(load.load_id)}
                            className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                            onClick={e => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="px-2">
                          <span className="text-[11px] font-medium text-brand-accent">
                            {load.load_id?.slice(-6)}
                          </span>
                        </TableCell>
                        <TableCell className="px-2">
                          {load.status === 'in_transit' && (
                            <AlertTriangle className="w-3 h-3 text-orange-400" title="In transit" />
                          )}
                        </TableCell>
                        <TableCell className="px-2">
                          <span className="text-[10px] text-muted-foreground">
                            {(load.delivery_type || 'Reg').slice(0, 3).toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[100px] truncate">
                          {getDriverName(load.driver_id)}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[120px] truncate">
                          {load.pickup_name || '--'}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[140px] truncate">
                          {pickup.street}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2">
                          {pickup.city}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[120px] truncate">
                          {load.delivery_name || '--'}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[140px] truncate">
                          {delivery.street}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2">
                          {delivery.city}
                        </TableCell>
                        <TableCell className="px-2">
                          <Badge className={`text-[9px] px-1.5 py-0 ${getLoadStatusClasses(load.status)}`}>
                            {STATUS_LABELS[load.status] || load.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2 text-right">
                          {load.weight_lbs ? `${load.weight_lbs.toLocaleString()}` : '--'}
                        </TableCell>
                        <TableCell className="text-[11px] text-foreground font-medium px-2 text-right">
                          {load.rate ? `$${Number(load.rate).toFixed(2)}` : '--'}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground px-2">
                          {formatDate(load.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {activeLoads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={15} className="text-center py-8 text-muted-foreground text-xs">
                        No loads in this category
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
